import { utils, writeFile } from "xlsx";
import JsPDF from "jspdf";
import Papa from "papaparse";
import { Toolbar } from "./Toolbar";
import classNames from "classnames";
import { Pagination } from "./Pagination";
import autoTable from "jspdf-autotable";
import { Spinner, Modal } from "../../components";
import { useExportData } from "react-table-plugins";
import { useRef, useEffect, forwardRef, useState, useImperativeHandle } from "react";
import { Row as RowType, Col, Button } from "react-bootstrap";
import { useTable, useSortBy, usePagination, useRowSelect, useGlobalFilter, useExpanded, Column, Row } from "react-table";
import { Table as NewTable } from "react-bootstrap";
import { debug } from "console";
import { onlyUnique } from "helpers/Functions";

export type CellFormatter<T extends Object = {}> = { row: Row<T>; };

type IndeterminateCheckboxProps = {
    indeterminate: any;
    children?: React.ReactNode;
    handleSelectedRows?: any;
};

const IndeterminateCheckbox = forwardRef<HTMLInputElement, IndeterminateCheckboxProps>(({ indeterminate, ...rest }, ref) => {
    const defaultRef = useRef();
    const resolvedRef: any = ref || defaultRef;

    useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
        <div className="form-check">
            <input type="checkbox" className="form-check-input" ref={resolvedRef} {...rest} />
            <label htmlFor="form-check-input" className="form-check-label"></label>
        </div>
    );
}
);

type TableProps<TableValues> = {
    isSearchable?: boolean;
    isSortable?: boolean;
    pagination?: boolean;
    isSelectable?: boolean;
    isExpandable?: boolean;
    columns: ReadonlyArray<Column>;
    data: TableValues[];
    pageSize?: number;
    searchBoxClass?: string;
    tableClass?: string;
    theadClass?: string;
    hideToolbar?: boolean;
    addNewRow?: () => void;
    reloadTableRows?: () => void;
    importProducts?: () => void;
    singleImport?: () => void;
    removeRows?: () => void;
    onSearchClick?: (keyword? : string) => void;
    searchColumns?: string[];
    onQuickAdd?: (value: string) => void;
    allowQuickAdd?: boolean;
    handleDelete?: (rows: any) => void;
    handleSelectedRows?: (rows: any) => void;
    selectedRowsUpdate?: (rows: any) => void;
    selectedRows?: (rows: any) => void;
    disableExport?: boolean;
    autoHeight?: boolean;
    maxHeight?: number;
    title?: React.ReactNode;
    className?: string;
    canDelete?: boolean;
    syncOptions?: () => void;
    loading?: boolean;
    errors?: string;
    // Extra Components Props
    topbarChild?: React.ReactNode;
    filterComponent?: React.ReactNode;
    disableAutoSelectReset?: boolean;
};

export interface TableRef {
    selectRow: (rowId: string) => void;
}

const Table = forwardRef(<TableValues extends object = {}>(
    props: TableProps<TableValues>,
    ref: React.Ref<TableRef>
  ) => {
    const title = props["title"] || null;
    const errors = props["errors"] || "";
    const componentRef = useRef(null);
    const loading = props["loading"] || false;
    const className = props["className"] || "";
    const maxHeight = props["maxHeight"] || "";
    const isSortable = props["isSortable"] || false;
    const pagination = props["pagination"] || false;
    const topbarChild = props["topbarChild"] || null;
    const autoHeight = props["autoHeight"] || false;
    const isSelectable = props["isSelectable"] || false;
    const removeRows = props["removeRows"] || null;
    const isSearchable = props["isSearchable"] || false;
    const singleImport = props["singleImport"] || false;
    const handleDelete = props["handleDelete"] || null;
    const addNewRow = props["addNewRow"] || false;
    const selectedRows = props["selectedRows"] || null;
    const isExpandable = props["isExpandable"] || false;
    const disableExport = props["disableExport"] || false;
    const onSearchClick = props["onSearchClick"] || null;
    const searchColumns = props["searchColumns"] || null;
    const onQuickAdd = props["onQuickAdd"] || null;
    const allowQuickAdd = props["allowQuickAdd"] || null;
    const [showPopup, setShowPopup] = useState(false);
    const importProducts = props["importProducts"] || false;
    const filterComponent = props["filterComponent"] || null;
    const reloadTableRows = props["reloadTableRows"] || false;
    const handleSelectedRows = props["handleSelectedRows"] || null;
    const selectedRowsUpdate = props["selectedRowsUpdate"] || null;
    const canDelete = props["canDelete"] != undefined ? props["canDelete"] : true;
    const syncOptions = props["syncOptions"] || false;
    const sizePerPageList = [
        {
            text: "15",
            value: 15,
        },
        {
            text: "50",
            value: 50,
        },
        {
            text: "100",
            value: 100,
        },
        {
            text: "All",
            value: props["data"].length,
        },
    ];
    const [selectedRowsCount, setSelectedRowsCount] = useState(0);

    let otherProps: any = {};
    if (isSearchable) { otherProps["useGlobalFilter"] = useGlobalFilter; }
    if (isSortable) { otherProps["useSortBy"] = useSortBy; }
    if (isExpandable) { otherProps["useExpanded"] = useExpanded; }
    if (isSelectable) { otherProps["useRowSelect"] = useRowSelect; }
    otherProps["usePagination"] = usePagination;

    const dataTable = useTable(
        {
            columns: props["columns"],
            data: props["data"] || [],
            initialState: {
                pageSize: props["pageSize"] || 10,
            },
            title: title,
            errors: errors,
            loading: loading,
            canDelete: canDelete,
            autoResetSelectedRows: props.disableAutoSelectReset === true ? false : true,
            maxHeight: maxHeight,
            autoHeight: autoHeight,
            isSelectable: isSelectable,
            topbarChild: topbarChild,
            syncOptions: syncOptions,
            addNewRow: addNewRow,
            autoResetGlobalFilter: false,
            removeRows: removeRows,
            singleImport: singleImport,
            selectedRows: selectedRows,
            onSearchClick: onSearchClick,
            onQuickAdd: onQuickAdd,
            allowQuickAdd: allowQuickAdd,
            componentRef: componentRef,
            importProducts: importProducts,
            filterComponent: filterComponent,
            reloadTableRows: reloadTableRows,
            getExportFileBlob: getExportFileBlob,
            selectedRowsUpdate: selectedRowsUpdate,
            handleDelete: () => { setShowPopup(true) },
            handleSelectedRows: () => {
                // @ts-ignore                
                handleSelectedRows([...handleSelectedRows.map((row: any) => row.original)]);
            },
        },
        otherProps.hasOwnProperty("useGlobalFilter") && otherProps["useGlobalFilter"],
        otherProps.hasOwnProperty("useSortBy") && otherProps["useSortBy"],
        otherProps.hasOwnProperty("useExpanded") && otherProps["useExpanded"],
        otherProps.hasOwnProperty("usePagination") && otherProps["usePagination"],
        otherProps.hasOwnProperty("useRowSelect") && otherProps["useRowSelect"],
        useExportData,
        (hooks) => {
            isSelectable && hooks.visibleColumns.push((columns) => [
                {
                    id: "selection",
                    Header: ({ getToggleAllPageRowsSelectedProps }: any) => (
                        <div><IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} /></div>
                    ),
                    maxWidth: 20,
                    minWidth: 20,
                    width: 20,
                    Cell: ({ row }: any) => (
                        <div><IndeterminateCheckbox {...row.getToggleRowSelectedProps()} /></div>
                    ),
                },
                ...columns,
            ]);
        }
    );

    const { state: { selectedRowIds } } = dataTable;

     // Expose methods using useImperativeHandle
     useImperativeHandle(ref, () => ({
        selectRow: (rowId: string) => {
            if (dataTable.rowsById[rowId]) {
                dataTable.toggleRowSelected(rowId);
            } else {
                console.error(`Row with ID ${rowId} not found.`);
            }
        },
    }));
      

    useEffect(() => {
        let ids: any = [];
        if (selectedRows && Object.keys(dataTable.state.selectedRowIds).length > 0) {
            ids = Object.keys(dataTable.state.selectedRowIds);
            ids = ids.map((id: any) => parseInt(id));
        }
        if (selectedRows) {
            // @ts-ignore
            selectedRows(ids);
        }
    }, [dataTable.state.selectedRowIds]);

    useEffect(() => {
        if (selectedRowsUpdate && dataTable.data.length >= 0) {
            let selectedIndexes: any[] = [];
            dataTable.selectedFlatRows.forEach((row: any) => selectedIndexes.push(row.original.id));
            selectedRowsUpdate(selectedIndexes);
        }

        if(dataTable.state.selectedRowIds != undefined)
            setSelectedRowsCount(Object.keys(dataTable.state.selectedRowIds).length);

    }, [dataTable.state.selectedRowIds, dataTable.table]);

    // @ts-ignore
    function getExportFileBlob({ columns, data, fileType, fileName }) {
        if (fileType === "csv") {
            const headerNames = columns.map((col: any) => col.exportValue);
            const csvString = Papa.unparse({ fields: headerNames, data });
            return new Blob([csvString], { type: "text/csv" });
        } else if (fileType === "xlsx") {
            const header = columns.map((c: any) => c.exportValue);
            const compatibleData = data.map((row: any) => {
                const obj: any = {};
                header.forEach((col: any, index: any) => {
                    obj[col] = row[index];
                });
                return obj;
            });
            let wb = utils.book_new();
            let ws1 = utils.json_to_sheet(compatibleData, {
                header,
            });
            utils.book_append_sheet(wb, ws1, "React Table Data");
            writeFile(wb, `${fileName}.xlsx`);
            return false;
        }
        if (fileType === "pdf") {
            const headerNames = columns.map((column: any) => column.exportValue);
            const doc = new JsPDF();
            autoTable(doc, {
                head: [headerNames],
                body: data,
                margin: { top: 20 },
                styles: {
                    minCellHeight: 9,
                    halign: "left",
                    fontSize: 11,
                },
            });
            doc.save(`${fileName}.pdf`);
            return false;
        }
        return false;
    }

    let rows = pagination ? dataTable.page : dataTable.rows;

    return (
        <>
            {props.hideToolbar != true && <Toolbar dataTable={dataTable} handleDelete={handleDelete} canDelete={canDelete} isSearchable={isSearchable} disableExport={disableExport} selectedRowCount={selectedRowsCount} searchColumns={searchColumns} />}
            <div
                className={classNames("table-responsive", "scrollable-container", className, autoHeight ? "ztable-auto" : "")}
                style={{ height: `${maxHeight}px` || "", overflow: "auto" }}
                ref={componentRef}
            >
                <NewTable
                    {...dataTable.getTableProps()}
                    className={classNames("table table-centered react-table", props["tableClass"])}
                >
                    <thead className={props["theadClass"]}>
                        {dataTable.headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column: any) => (
                                    <th
                                        {...column.getHeaderProps(
                                            column.defaultCanSort && column.getSortByToggleProps()
                                        )}

                                        {...column.getHeaderProps({
                                            style: { minWidth: column.minWidth, width: column.width },
                                        })}

                                        className={classNames({
                                            sorting_desc: column.isSortedDesc === true,
                                            sorting_asc: column.isSortedDesc === false,
                                            sortable: column.defaultCanSort === true,
                                        })}
                                    >
                                        {column.render("Header")}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...dataTable.getTableBodyProps()}>
                        {(rows || []).map((row: any, i: any) => {
                            dataTable.prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map((cell: any) => {
                                        return <td {...cell.getCellProps()}>{cell.render("Cell")}</td>;
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </NewTable>
                {rows.length === 0 && (
                    <div className={classNames("text-center d-flex", autoHeight ? "" : "no-data")} style={{ flexDirection: "column" }}>

                        {dataTable.errors === "" && dataTable.loading === false && <>
                            <p className="text-muted mb-0">No data to display</p>
                        </>}

                        {dataTable.loading === true && (<>
                            <Spinner className="m-2" color="primary" />
                            <p className="text-muted mb-0 mt-2">Loading...</p>
                        </>)}

                        {dataTable.errors && dataTable.loading === false && <>
                            <i className="mdi mdi-alert mdi-18px" style={{ color: "red" }}></i>
                            <p className="text-muted mb-0">{dataTable.errors}</p>
                        </>}

                    </div>
                )}

            </div>

            {pagination && <Pagination tableProps={dataTable} sizePerPageList={sizePerPageList} />}


            {handleSelectedRows &&
                <RowType className="mt-3">
                    <Col sm={6}>
                    </Col>
                    <Col sm={6} className="d-flex flex-row-reverse">
                        <Button variant="primary" className="btn" onClick={
                            () => {
                                handleSelectedRows([...dataTable.selectedFlatRows.map((row: any) => row.original)]);
                            }
                        }>Save Selected</Button>
                    </Col>
                </RowType>
            }
 
            {dataTable.removeRows !== false && dataTable.selectedFlatRows && dataTable.selectedFlatRows.length > 0 &&
                <Modal
                    title="Confirm"
                    show={showPopup}
                    buttonText="Delete"
                    buttonType="danger"
                    buttonCloseText="Cancel"
                    handleClose={() => setShowPopup(false)}
                    handleSubmit={() => {
                        setShowPopup(false);
                        // @ts-ignore
                        removeRows();
                    }}
                    description={
                        <>
                            <p>You are about to delete {dataTable.selectedFlatRows.length} selected {dataTable.selectedFlatRows.length > 1 ? "items": "item" }.</p>
                            <p>Are you sure you want to delete?</p>
                        </>
                    }
                />
            }
        </>
    );
});

export { Table };
